'use client';

import { useGSAP } from '@gsap/react';
import clsx from 'clsx';
import { FC, useRef } from 'react';

import { AnimatedBackground } from '@/components/AnimatedBackground';
import PageBreadcrumbs from '@/components/PageBreadcrumbs';
import { ServicesLobbyHeroDto } from '@/components/ServicesLobbyPage/ServicesLobbyHero/types';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import useDevice from '@/hooks/useDevice';
import { useViewportVideosPlayback } from '@/hooks/useViewportVideosPlayback';
import { breadcrumbTrails } from '@/lib/breadcrumbs';
import { gsap } from '@/lib/gsap';

import css from './style.module.scss';

interface Props {
	data: ServicesLobbyHeroDto;
}

const ServicesLobbyHero: FC<Props> = ({ data }: Props) => {
	const device = useDevice();
	const isMobile = device === 'mobile';
	const wrapperRef = useRef<HTMLElement>(null);
	const backgroundRef = useRef<HTMLDivElement>(null);
	const videoWrapperRef = useRef<HTMLDivElement>(null);
	const breadcrumbsRef = useRef<HTMLDivElement>(null);

	useViewportVideosPlayback([wrapperRef]);

	useGSAP(
		() => {
			if (!wrapperRef.current) return;

			const breadcrumbs = breadcrumbsRef.current;
			const titleLines = wrapperRef.current.querySelectorAll(`.${css.title} p`);
			const description = wrapperRef.current.querySelector(`.${css.description}`);

			const tl = gsap.timeline({
				defaults: { ease: 'power3.out', duration: 1 }
			});

			gsap.set([titleLines, description], {
				opacity: 0,
				y: 40,
				filter: 'blur(10px)'
			});

			if (breadcrumbs)
				gsap.set(breadcrumbs, {
					opacity: 0,
					y: 40,
					filter: 'blur(10px)'
				});

			tl.to(backgroundRef.current, {
				opacity: 1,
				duration: 1.5
			})
				.to(
					videoWrapperRef.current,
					{
						opacity: 1,
						scale: 1.1,
						duration: 1.5
					},
					'-=1.2'
				)
				.to(
					titleLines,
					{
						opacity: 1,
						y: 0,
						filter: 'blur(0px)',
						stagger: 0.15,
						duration: 1.1
					},
					'-=1'
				)
				.to(
					description,
					{
						opacity: 1,
						y: 0,
						filter: 'blur(0px)',
						duration: 1
					},
					'-=0.8'
				);

			if (breadcrumbs)
				tl.to(
					breadcrumbs,
					{
						opacity: 1,
						y: 0,
						filter: 'blur(0px)',
						duration: 1,
						ease: 'power2.out'
					},
					'-=0.6'
				);
		},
		{ scope: wrapperRef }
	);

	return (
		<section ref={wrapperRef} className={css.wrapper}>
			<div className={clsx('container', css.content)}>
				<PageBreadcrumbs
					ref={breadcrumbsRef}
					items={breadcrumbTrails.servicesLobby()}
					withContainer={false}
					wrapperClassName={css.breadcrumbsWrap}
					className={css.breadcrumbs}
				/>
				<Typography uppercase className={css.title} variant={isMobile ? 'h2' : 'h1'} tag="h1">
					<StrapiRichContent content={data.title} />
				</Typography>
				<Typography className={css.description} variant={isMobile ? 'body-s' : 'body-m'} tag="div">
					<StrapiRichContent content={data.description} />
				</Typography>

				<div ref={videoWrapperRef} className={css.videoWrapper}>
					<video autoPlay loop muted playsInline className={css.video} controls={false}>
						<source
							src="/assets/video/mov/keyboard-green.mov"
							type='video/quicktime; codecs="hvc1"'
						/>
						<source src="/assets/video/webm/keyboard-green.webm" type="video/webm" />
					</video>
				</div>
			</div>

			<div ref={backgroundRef} className={css.animatedBackground}>
				<AnimatedBackground
					brightColors
					theme="green"
					staticBlobsPosition={isMobile ? 'sides-center' : 'top-right_bottom-left'}
				/>
			</div>
		</section>
	);
};

export default ServicesLobbyHero;
